import React, { useState, useEffect } from 'react';
import { Cloud, Sun, CloudRain, CloudSnow, CloudLightning, Wind, Droplets, MapPin } from 'lucide-react';

const WeatherSlide = ({ zipCode }) => {
  const [weather, setWeather] = useState(null);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWeather = async (latitude, longitude, locationName) => {
      try {
        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=auto`
        );
        const data = await response.json();
        
        if (locationName) {
          setLocation(locationName);
        } else {
          try {
            const nominatimResponse = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
            );
            const nominatimData = await nominatimResponse.json();
            const city = nominatimData.address?.city || nominatimData.address?.town || nominatimData.address?.village || 'Unknown Location';
            const state = nominatimData.address?.state || '';
            setLocation(`${city}${state ? ', ' + state : ''}`);
          } catch {
            setLocation('Current Location');
          }
        }
        
        setWeather({
          temperature: Math.round(data.current.temperature_2m),
          humidity: data.current.relative_humidity_2m,
          windSpeed: Math.round(data.current.wind_speed_10m),
          weatherCode: data.current.weather_code,
        });
        setLoading(false);
      } catch (err) {
        console.error('Weather fetch error:', err);
        setError('Unable to load weather');
        setLoading(false);
      }
    };

    const fetchByZipCode = async (zip) => {
      try {
        const response = await fetch(`https://api.zippopotam.us/us/${zip}`);
        if (!response.ok) throw new Error('Invalid zip code');
        const data = await response.json();
        const place = data.places?.[0];
        if (place) {
          const lat = parseFloat(place.latitude);
          const lon = parseFloat(place.longitude);
          const name = `${place['place name']}, ${place['state abbreviation']}`;
          fetchWeather(lat, lon, name);
        } else {
          throw new Error('No location found');
        }
      } catch (err) {
        console.error('Zip code lookup failed:', err);
        // Fallback to geolocation
        fetchWithGeolocation();
      }
    };

    const fetchWithGeolocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            fetchWeather(position.coords.latitude, position.coords.longitude);
          },
          () => {
            fetchWeather(44.9778, -93.2650, 'Minneapolis, MN');
          },
          { timeout: 10000 }
        );
      } else {
        fetchWeather(44.9778, -93.2650, 'Minneapolis, MN');
      }
    };

    // If zip code is provided, use it; otherwise fall back to geolocation
    if (zipCode && zipCode.trim()) {
      fetchByZipCode(zipCode.trim());
    } else {
      fetchWithGeolocation();
    }

    // Refresh weather every 30 minutes
    const interval = setInterval(() => {
      if (zipCode && zipCode.trim()) {
        fetchByZipCode(zipCode.trim());
      } else {
        fetchWithGeolocation();
      }
    }, 30 * 60 * 1000);

    return () => clearInterval(interval);
  }, [zipCode]);

  const getWeatherInfo = (code) => {
    const weatherMap = {
      0: { icon: Sun, description: 'Clear Sky', type: 'clear' },
      1: { icon: Sun, description: 'Mainly Clear', type: 'clear' },
      2: { icon: Cloud, description: 'Partly Cloudy', type: 'cloudy' },
      3: { icon: Cloud, description: 'Overcast', type: 'cloudy' },
      45: { icon: Cloud, description: 'Foggy', type: 'cloudy' },
      48: { icon: Cloud, description: 'Rime Fog', type: 'cloudy' },
      51: { icon: CloudRain, description: 'Light Drizzle', type: 'rain' },
      53: { icon: CloudRain, description: 'Drizzle', type: 'rain' },
      55: { icon: CloudRain, description: 'Heavy Drizzle', type: 'rain' },
      61: { icon: CloudRain, description: 'Light Rain', type: 'rain' },
      63: { icon: CloudRain, description: 'Rain', type: 'rain' },
      65: { icon: CloudRain, description: 'Heavy Rain', type: 'rain' },
      71: { icon: CloudSnow, description: 'Light Snow', type: 'snow' },
      73: { icon: CloudSnow, description: 'Snow', type: 'snow' },
      75: { icon: CloudSnow, description: 'Heavy Snow', type: 'snow' },
      77: { icon: CloudSnow, description: 'Snow Grains', type: 'snow' },
      80: { icon: CloudRain, description: 'Rain Showers', type: 'rain' },
      81: { icon: CloudRain, description: 'Rain Showers', type: 'rain' },
      82: { icon: CloudRain, description: 'Heavy Showers', type: 'rain' },
      85: { icon: CloudSnow, description: 'Snow Showers', type: 'snow' },
      86: { icon: CloudSnow, description: 'Heavy Snow', type: 'snow' },
      95: { icon: CloudLightning, description: 'Thunderstorm', type: 'storm' },
      96: { icon: CloudLightning, description: 'Thunderstorm', type: 'storm' },
      99: { icon: CloudLightning, description: 'Severe Storm', type: 'storm' },
    };
    return weatherMap[code] || { icon: Cloud, description: 'Unknown', type: 'cloudy' };
  };

  if (loading) {
    return (
      <div className="w-full h-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <p className="text-white text-2xl font-light animate-pulse">Loading Weather...</p>
        </div>
      </div>
    );
  }

  if (error || !weather) {
    return (
      <div className="w-full h-full bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <Cloud className="w-32 h-32 text-slate-600 mx-auto mb-4 animate-pulse" />
          <p className="text-white text-2xl">Weather Unavailable</p>
        </div>
      </div>
    );
  }

  const weatherInfo = getWeatherInfo(weather.weatherCode);
  const WeatherIcon = weatherInfo.icon;
  const weatherType = weatherInfo.type;

  const now = new Date();
  const timeString = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  const dateString = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  // Generate particles based on weather
  const particles = [];
  if (weatherType === 'rain') {
    for (let i = 0; i < 50; i++) {
      particles.push(
        <div
          key={i}
          className="absolute w-0.5 h-8 bg-gradient-to-b from-cyan-400/60 to-transparent rounded-full animate-rain"
          style={{
            left: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 2}s`,
            animationDuration: `${0.5 + Math.random() * 0.5}s`
          }}
        />
      );
    }
  } else if (weatherType === 'snow') {
    for (let i = 0; i < 40; i++) {
      particles.push(
        <div
          key={i}
          className="absolute w-2 h-2 bg-white/80 rounded-full animate-snow"
          style={{
            left: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${3 + Math.random() * 4}s`
          }}
        />
      );
    }
  }

  return (
    <div className="w-full h-full relative overflow-hidden">
      {/* Animated Background */}
      <div className={`absolute inset-0 transition-all duration-1000 ${
        weatherType === 'clear' ? 'bg-gradient-to-br from-orange-400 via-pink-500 to-purple-600' :
        weatherType === 'cloudy' ? 'bg-gradient-to-br from-slate-600 via-slate-700 to-slate-800' :
        weatherType === 'rain' ? 'bg-gradient-to-br from-slate-700 via-blue-900 to-slate-900' :
        weatherType === 'snow' ? 'bg-gradient-to-br from-slate-400 via-blue-200 to-slate-500' :
        weatherType === 'storm' ? 'bg-gradient-to-br from-purple-900 via-slate-900 to-black' :
        'bg-gradient-to-br from-slate-700 to-slate-900'
      }`}>
        {/* Animated gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
        
        {/* Animated orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl animate-float-delayed" />
        <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl animate-float-slow" />
      </div>

      {/* Weather Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles}
      </div>

      {/* Lightning effect for storms */}
      {weatherType === 'storm' && (
        <div className="absolute inset-0 bg-white/0 animate-lightning pointer-events-none" />
      )}

      {/* Content */}
      <div className="relative z-10 w-full h-full flex flex-col items-center justify-center p-8">
        {/* Location */}
        <div className="flex items-center gap-3 text-white/90 text-2xl mb-2 animate-fade-in">
          <MapPin className="w-6 h-6 animate-bounce-slow" />
          <span className="font-light tracking-wide">{location}</span>
        </div>
        
        {/* Date */}
        <p className="text-white/70 text-xl mb-1 animate-fade-in-delayed">{dateString}</p>
        
        {/* Time */}
        <p className="text-white text-7xl font-extralight tracking-wider mb-12 animate-fade-in-delayed-2">
          {timeString}
        </p>

        {/* Main Weather Display */}
        <div className="flex items-center justify-center gap-16 mb-12">
          {/* Icon */}
          <div className="relative">
            <div className="absolute inset-0 blur-2xl bg-white/20 rounded-full scale-150 animate-pulse-slow" />
            <WeatherIcon className={`relative w-48 h-48 text-white drop-shadow-2xl ${
              weatherType === 'clear' ? 'animate-spin-slow text-yellow-300' :
              weatherType === 'storm' ? 'animate-shake' :
              'animate-float'
            }`} />
          </div>
          
          {/* Temperature */}
          <div className="text-center">
            <div className="text-white text-[10rem] font-thin leading-none tracking-tighter drop-shadow-lg">
              {weather.temperature}
              <span className="text-6xl align-top">°F</span>
            </div>
            <p className="text-white/80 text-3xl font-light mt-2 tracking-wide">
              {weatherInfo.description}
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="flex gap-20">
          <div className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-2xl px-8 py-4 border border-white/20">
            <Droplets className="w-10 h-10 text-cyan-300 animate-bounce-slow" />
            <div>
              <p className="text-white text-4xl font-light">{weather.humidity}%</p>
              <p className="text-white/60 text-lg">Humidity</p>
            </div>
          </div>
          <div className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-2xl px-8 py-4 border border-white/20">
            <Wind className="w-10 h-10 text-cyan-300 animate-wiggle" />
            <div>
              <p className="text-white text-4xl font-light">{weather.windSpeed} mph</p>
              <p className="text-white/60 text-lg">Wind Speed</p>
            </div>
          </div>
        </div>

        {/* Branding */}
        <div className="absolute bottom-6 right-8 flex items-center gap-2">
          <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" />
          <span className="text-white/50 text-sm font-light tracking-wider">Powered by FrostByte IT</span>
        </div>
      </div>

      {/* Inline styles for animations */}
      <style>{`
        @keyframes rain {
          0% { transform: translateY(-100px); opacity: 0; }
          10% { opacity: 1; }
          100% { transform: translateY(100vh); opacity: 0; }
        }
        @keyframes snow {
          0% { transform: translateY(-50px) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          100% { transform: translateY(100vh) rotate(360deg); opacity: 0; }
        }
        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(30px, -30px) scale(1.1); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-20px, 20px) scale(1.05); }
        }
        @keyframes float-slow {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(15px, -15px); }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        @keyframes wiggle {
          0%, 100% { transform: rotate(-5deg); }
          50% { transform: rotate(5deg); }
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }
        @keyframes lightning {
          0%, 89%, 91%, 93%, 95%, 100% { background-color: transparent; }
          90%, 92%, 94% { background-color: rgba(255,255,255,0.3); }
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fade-in-delayed {
          0%, 20% { opacity: 0; transform: translateY(-20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes fade-in-delayed-2 {
          0%, 40% { opacity: 0; transform: translateY(-20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-rain { animation: rain linear infinite; }
        .animate-snow { animation: snow linear infinite; }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-float-delayed { animation: float-delayed 8s ease-in-out infinite; }
        .animate-float-slow { animation: float-slow 10s ease-in-out infinite; }
        .animate-spin-slow { animation: spin-slow 20s linear infinite; }
        .animate-shake { animation: shake 0.5s ease-in-out infinite; }
        .animate-wiggle { animation: wiggle 2s ease-in-out infinite; }
        .animate-bounce-slow { animation: bounce-slow 2s ease-in-out infinite; }
        .animate-pulse-slow { animation: pulse-slow 3s ease-in-out infinite; }
        .animate-lightning { animation: lightning 4s ease-in-out infinite; }
        .animate-fade-in { animation: fade-in 1s ease-out forwards; }
        .animate-fade-in-delayed { animation: fade-in-delayed 1.5s ease-out forwards; }
        .animate-fade-in-delayed-2 { animation: fade-in-delayed-2 2s ease-out forwards; }
      `}</style>
    </div>
  );
};

export default WeatherSlide;
